import * as React from "react";
import { VideoProjectCoachConfig } from "@/interfaces";
import {
  Dialog,
  Table,
  Button,
  SimpleSelectPrimitive,
  Spinner,
  PlayerCell,
  Calendar,
  Popover,
  Field,
  ButtonGroup,
  Skeleton,
  Input,
} from "@/components/ui";
import { VideoProjectServices as LegacyVideoProjectServices } from "@/services";
import { VideoProjectServices as BedrockVideoProjectServices } from "@/services/bedrock";
import { VideoCatalystUsageReport } from "@/services/bedrock/dtos";
import { useEffect, useMemo, useState } from "react";
import { exportToXlsx, XlsxColumnDef } from "@/utils/xlsxExportHelpers";
import { format, startOfDay, endOfDay, differenceInDays } from "date-fns";
import { CalendarIcon, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface OwnProps {
  coachId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BillingCycleOption {
  label: string;
  value: string;
}

const VideoCreditUsageModal: React.FC<OwnProps> = ({ open, onOpenChange, coachId }) => {
  const [coachConfig, setCoachConfig] = useState<VideoProjectCoachConfig>();
  const [reportType, setReportType] = useState<"cycle" | "custom">("cycle");
  const [selectedCycleValue, setSelectedCycleValue] = useState<string>();
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | undefined>();
  const [searchTerm, setSearchTerm] = useState("");

  const [report, setReport] = useState<VideoCatalystUsageReport>();
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const loadCoachConfig = async () => {
    if (coachId) {
      setIsConfigLoading(true);
      try {
        const config = await LegacyVideoProjectServices.getCoachConfig(coachId);
        setCoachConfig(config);
      } catch (error) {
        console.error("Failed to load coach config", error);
      } finally {
        setIsConfigLoading(false);
      }
    }
  };

  const loadReport = async () => {
    if (!coachId || !coachConfig) return;

    let start: Date;
    let end: Date;

    if (reportType === "cycle") {
      if (!selectedCycleValue) return;
      start = new Date(selectedCycleValue);
      const nextCycleStart = new Date(start.getFullYear(), start.getMonth() + 1, coachConfig.newCycleDay);
      end = new Date(nextCycleStart.getTime());
      end.setDate(end.getDate() - 1);
    } else {
      if (!dateRange?.from || !dateRange?.to) return;
      start = dateRange.from;
      end = dateRange.to;
    }

    setIsLoading(true);
    setReport(undefined);

    try {
      const reportData = await BedrockVideoProjectServices.getCreditUsageReport({
        startDate: startOfDay(start).toISOString(),
        endDate: endOfDay(end).toISOString(),
      });
      setReport(reportData);
    } catch (error) {
      console.error("Failed to load usage report", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset and load config on open
  useEffect(() => {
    if (open && coachId) {
      setCoachConfig(undefined);
      setSelectedCycleValue(undefined);
      setReport(undefined);
      setReportType("cycle");
      setSearchTerm("");
      loadCoachConfig();
    }
  }, [open, coachId]);

  // Load report when parameters change
  useEffect(() => {
    if (open && coachConfig && (reportType === "custom" || selectedCycleValue)) {
      loadReport();
    }
  }, [open, selectedCycleValue, dateRange, reportType, coachConfig]);

  const billingCycleOptions = useMemo<BillingCycleOption[]>(() => {
    if (coachConfig?.newCycleDay) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();

      const options = [];

      let cycleStartDate = new Date(currentYear, currentMonth, coachConfig.newCycleDay);
      if (cycleStartDate > today) {
        cycleStartDate.setMonth(cycleStartDate.getMonth() - 1);
      }

      for (let i = 0; i < 13; i++) {
        const nextMonth = new Date(
          cycleStartDate.getFullYear(),
          cycleStartDate.getMonth() + 1,
          coachConfig.newCycleDay
        );
        const cycleEndDate = new Date(nextMonth.getTime());
        cycleEndDate.setDate(cycleEndDate.getDate() - 1);

        options.push({
          label: `${cycleStartDate.toLocaleDateString()} - ${cycleEndDate.toLocaleDateString()}`,
          value: cycleStartDate.toISOString(),
        });
        cycleStartDate.setMonth(cycleStartDate.getMonth() - 1);
      }

      return options;
    } else {
      return [];
    }
  }, [coachConfig?.newCycleDay]);

  // Auto-select initial cycle when options are available
  useEffect(() => {
    if (open && billingCycleOptions.length > 0 && !selectedCycleValue && reportType === "cycle") {
      const today = new Date();
      const firstOptionDate = new Date(billingCycleOptions[0].value);

      let initialValue: string;
      // If we are within the first 4 days of the new cycle, select the previous one
      if (differenceInDays(today, firstOptionDate) < 4 && billingCycleOptions.length > 1) {
        initialValue = billingCycleOptions[1].value;
      } else {
        initialValue = billingCycleOptions[0].value;
      }
      setSelectedCycleValue(initialValue);
    }
  }, [open, billingCycleOptions, selectedCycleValue, reportType]);

  const filteredPlayerUsage = useMemo(() => {
    if (!report?.playerUsage) return [];
    if (!searchTerm) return report.playerUsage;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return report.playerUsage.filter(
      p =>
        p.playerName.toLowerCase().includes(lowerSearchTerm) ||
        p.playerEmail.toLowerCase().includes(lowerSearchTerm) ||
        p.internalIdentifier?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [report?.playerUsage, searchTerm]);

  const reportMetadata = useMemo(() => {
    if (!report || !coachConfig) return null;

    let start: Date;
    let end: Date;

    if (reportType === "cycle") {
      if (!selectedCycleValue) return null;
      start = new Date(selectedCycleValue);
      const nextCycleStart = new Date(start.getFullYear(), start.getMonth() + 1, coachConfig.newCycleDay);
      end = new Date(nextCycleStart.getTime());
      end.setDate(end.getDate() - 1);
    } else {
      if (!dateRange?.from || !dateRange?.to) return null;
      start = dateRange.from;
      end = dateRange.to;
    }

    const exportData = report.playerUsage.map(p => ({
      Player: p.playerName,
      Email: p.playerEmail,
      "Internal ID": p.internalIdentifier || "",
      "VC Enabled": p.currentlyEnabled ? "Yes" : "No",
      "Credit Limit": p.currentCreditLimit || "Unlimited",
      "Credits Used": p.creditsUsed,
      Regions: p.regionNames?.join(", ") || "",
      Branches: p.branchNames?.join(", ") || "",
      "Cost Centers": p.costCenterNames?.join(", ") || "",
      "Compliance Disclaimer": p.complianceDisclaimer || "",
    }));

    const totalCreditsUsed = report.playerUsage.reduce((acc, p) => acc + p.creditsUsed, 0);

    const meta = {
      reportName: "Video Credit Usage Report",
      description: `Period: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      generatedAt: new Date(),
    };

    const columns: XlsxColumnDef[] = [
      { header: "Player", key: "Player", wch: 30 },
      { header: "Email", key: "Email", wch: 30 },
      { header: "Internal ID", key: "Internal ID", wch: 15 },
      { header: "VC Enabled", key: "VC Enabled", wch: 12 },
      { header: "Credit Limit", key: "Credit Limit", wch: 15 },
      { header: "Credits Used", key: "Credits Used", wch: 15, type: "integer" as const },
      { header: "Regions", key: "Regions", wch: 25 },
      { header: "Branches", key: "Branches", wch: 25 },
      { header: "Cost Centers", key: "Cost Centers", wch: 25 },
      { header: "Compliance Disclaimer", key: "Compliance Disclaimer", wch: 40 },
    ];

    const filename = `Video_Credit_Usage_${format(start, "yyyy-MM-dd")}_to_${format(end, "yyyy-MM-dd")}`;

    return { exportData, meta, columns, filename, totalCreditsUsed };
  }, [report, selectedCycleValue, dateRange, reportType, coachConfig]);

  const handleExport = async () => {
    if (!reportMetadata) return;
    setIsExporting(true);
    try {
      const { filename, meta, columns, exportData, totalCreditsUsed } = reportMetadata;

      const sheets = [
        {
          name: "Video Credit Usage",
          meta: {
            ...meta,
            totalRecords: totalCreditsUsed,
            totalRecordsLabel: "Total Credits Used",
          },
          sections: [
            {
              title: "Usage Details",
              columns,
              rows: exportData,
            },
          ],
        },
      ];

      await exportToXlsx(filename, sheets);
    } catch (error) {
      console.error("Failed to export to XLSX", error);
    } finally {
      setIsExporting(false);
    }
  };

  const totalCredits = filteredPlayerUsage.reduce((acc, p) => acc + p.creditsUsed, 0);
  const enabledCount = filteredPlayerUsage.filter(p => p.currentlyEnabled).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl" showCloseButton>
      <Dialog.Header>
        <Dialog.Title>Video Credit Usage</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6 mb-2">
            <Field className="w-auto">
              <Field.Label className="text-[11px] font-bold text-slate-500 uppercase tracking-tight">
                Report Type
              </Field.Label>
              <ButtonGroup>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    "h-10 px-4",
                    reportType === "cycle"
                      ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                      : "text-slate-600"
                  )}
                  onClick={() => setReportType("cycle")}
                  aria-pressed={reportType === "cycle"}
                >
                  Billing Cycle
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    "h-10 px-4",
                    reportType === "custom"
                      ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                      : "text-slate-600"
                  )}
                  onClick={() => setReportType("custom")}
                  aria-pressed={reportType === "custom"}
                >
                  Custom Range
                </Button>
              </ButtonGroup>
            </Field>

            <Field className="flex-1 max-w-sm">
              <Field.Label className="text-[11px] font-bold text-slate-500 uppercase tracking-tight">
                {reportType === "cycle" ? "Select Cycle" : "Select Date Range"}
              </Field.Label>
              <div className="flex items-center gap-3">
                {reportType === "cycle" ? (
                  <Skeleton loading={isConfigLoading} className="h-10 w-full">
                    <SimpleSelectPrimitive value={selectedCycleValue} onValueChange={setSelectedCycleValue}>
                      <SimpleSelectPrimitive.Trigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm">
                        <SimpleSelectPrimitive.Value placeholder="Select Billing Cycle">
                          {billingCycleOptions.find(o => o.value === selectedCycleValue)?.label}
                        </SimpleSelectPrimitive.Value>
                      </SimpleSelectPrimitive.Trigger>
                      <SimpleSelectPrimitive.Content>
                        {billingCycleOptions.map(o => (
                          <SimpleSelectPrimitive.Item key={o.value} value={o.value}>
                            {o.label}
                          </SimpleSelectPrimitive.Item>
                        ))}
                      </SimpleSelectPrimitive.Content>
                    </SimpleSelectPrimitive>
                  </Skeleton>
                ) : (
                  <Popover>
                    <Popover.Trigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal h-10 shadow-sm",
                          !dateRange && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                            </>
                          ) : (
                            format(dateRange.from, "LLL dd, y")
                          )
                        ) : (
                          <span>Pick a date range</span>
                        )}
                      </Button>
                    </Popover.Trigger>
                    <Popover.Content className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange ? { from: dateRange.from, to: dateRange.to } : undefined}
                        onSelect={(range: any) => setDateRange(range)}
                        numberOfMonths={2}
                      />
                    </Popover.Content>
                  </Popover>
                )}
                {isLoading && <Spinner className="text-primary shrink-0" />}
              </div>
            </Field>

            <Field className="flex-1 max-w-sm">
              <Field.Label className="text-[11px] font-bold text-slate-500 uppercase tracking-tight">
                Search Players
              </Field.Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  className="pl-9 h-10"
                  value={searchTerm}
                  placeholder="Search by name, email or ID..."
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </Field>
          </div>

          <Table wrapperClassName="max-h-[50vh] overflow-y-auto relative text-sm border rounded-md">
            <Table.Header className="sticky top-0 bg-background z-20">
              <Table.Row className="bg-background">
                <Table.Head>Player</Table.Head>
                <Table.Head>VC Enabled</Table.Head>
                <Table.Head>Current Credit Limit</Table.Head>
                <Table.Head>Credits Used</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={4} className="h-40 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Spinner className="size-8 text-primary" />
                      <span className="text-muted-foreground">Loading usage data...</span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : filteredPlayerUsage.length > 0 ? (
                filteredPlayerUsage.map(playerUsage => (
                  <Table.Row key={`player-usage-${playerUsage.playerId}`}>
                    <Table.Cell>
                      <PlayerCell
                        player={{
                          id: playerUsage.playerId,
                          fullName: playerUsage.playerName,
                          email: playerUsage.playerEmail,
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell>{playerUsage.currentlyEnabled ? "Yes" : "No"}</Table.Cell>
                    <Table.Cell>{playerUsage.currentCreditLimit || "Unlimited"}</Table.Cell>
                    <Table.Cell>{playerUsage.creditsUsed}</Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={4} className="h-40 text-center text-muted-foreground">
                    {reportType === "custom" && (!dateRange?.from || !dateRange?.to)
                      ? "Please select a date range to view usage data."
                      : "No usage data found for the selected period."}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
            {!isLoading && filteredPlayerUsage.length > 0 && (
              <Table.Footer className="sticky bottom-0 bg-background z-20 border-t-2">
                <Table.Row className="bg-muted/50 font-bold">
                  <Table.Cell>Total</Table.Cell>
                  <Table.Cell>
                    {enabledCount} / {filteredPlayerUsage.length}
                  </Table.Cell>
                  <Table.Cell>--</Table.Cell>
                  <Table.Cell>{totalCredits}</Table.Cell>
                </Table.Row>
              </Table.Footer>
            )}
          </Table>
        </div>
      </Dialog.Content>
      <Dialog.Footer className="sm:justify-between items-center">
        <div className="text-xs text-muted-foreground">
          {report?.playerUsage ? `Showing ${filteredPlayerUsage.length} of ${report.playerUsage.length} players` : ""}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} disabled={!reportMetadata || isLoading || isExporting}>
            {isExporting ? <Spinner className="mr-2" /> : null}
            Export
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
};

export default VideoCreditUsageModal;
